import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsService } from '~/services/notifications';
import {
  NotificationListResponseDto,
  NotificationResponseDto,
} from '@bitecritic-api/src/notifications/dto/notification-response.dto';
import { useLocale } from '~/hooks/useLocale';
import useUserContext from './useUserContext';

// Query keys
const NOTIFICATIONS_COUNT_KEY = 'notifications-count';
const NOTIFICATIONS_LIST_KEY = 'notifications-list';
const NOTIFICATION_SETTINGS_KEY = 'notification-settings';
const LOCAL_STORAGE_COUNT_KEY = 'bitecritic-notification-count';

const setStoredCount = (count: number): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(LOCAL_STORAGE_COUNT_KEY, count.toString());
  } catch {
    // Silently fail if localStorage is not available
  }
};

/**
 * Hook for fetching the unread notification count
 */
export const useNotificationCount = () => {
  const { user } = useUserContext();

  return useQuery({
    queryKey: [NOTIFICATIONS_COUNT_KEY],
    queryFn: async () => {
      const count = await notificationsService.getUnreadCount();
      setStoredCount(count);
      return count;
    },
    enabled: !!user?.id,
  });
};

/**
 * Hook for fetching the notifications list
 */
export const useNotificationsList = (page = 1, limit = 10) => {
  const { currentLocale } = useLocale();

  return useQuery<NotificationListResponseDto>({
    queryKey: [NOTIFICATIONS_LIST_KEY, page, limit, currentLocale.language],
    queryFn: () =>
      notificationsService.getNotifications(
        page,
        limit,
        currentLocale.language,
      ),
  });
};

/**
 * Hook for marking a notification as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationsService.markAsRead(notificationId),
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_LIST_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_COUNT_KEY] });

      // Get previous data
      const previousCount = queryClient.getQueryData([
        NOTIFICATIONS_COUNT_KEY,
      ]) as number;

      // Optimistically update count
      const newCount = Math.max(0, previousCount - 1);
      setStoredCount(newCount);
      queryClient.setQueryData([NOTIFICATIONS_COUNT_KEY], newCount);

      // Optimistically update the notification in the list
      const previousData = queryClient.getQueryData([
        NOTIFICATIONS_LIST_KEY,
      ]) as NotificationListResponseDto | undefined;

      if (previousData) {
        const updatedData = {
          ...previousData,
          data: previousData.data.map((notification: NotificationResponseDto) =>
            notification.id === notificationId
              ? { ...notification, isRead: true }
              : notification,
          ),
        };

        queryClient.setQueryData([NOTIFICATIONS_LIST_KEY], updatedData);
      }

      return { previousCount, previousData };
    },
    onError: (error, variables, context) => {
      // Restore previous data
      if (context?.previousCount !== undefined) {
        setStoredCount(context.previousCount);
        queryClient.setQueryData(
          [NOTIFICATIONS_COUNT_KEY],
          context.previousCount,
        );
      }

      if (context?.previousData) {
        queryClient.setQueryData(
          [NOTIFICATIONS_LIST_KEY],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      // Invalidate queries to ensure data is fresh
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_COUNT_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_LIST_KEY] });
    },
  });
};

/**
 * Hook for marking all notifications as read
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_LIST_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_COUNT_KEY] });

      // Get previous data
      const previousData = queryClient.getQueryData([
        NOTIFICATIONS_LIST_KEY,
      ]) as NotificationListResponseDto | undefined;

      // Update local storage count
      setStoredCount(0);

      // Optimistically update counts
      queryClient.setQueryData([NOTIFICATIONS_COUNT_KEY], 0);

      // Optimistically update all notifications to read
      if (previousData) {
        const updatedData = {
          ...previousData,
          data: previousData.data.map(
            (notification: NotificationResponseDto) => ({
              ...notification,
              isRead: true,
            }),
          ),
        };

        queryClient.setQueryData([NOTIFICATIONS_LIST_KEY], updatedData);
      }

      return { previousData };
    },
    onError: (error, variables, context) => {
      // Restore previous data
      if (context?.previousData) {
        queryClient.setQueryData(
          [NOTIFICATIONS_LIST_KEY],
          context.previousData,
        );

        // Count unread notifications
        const unreadCount = context.previousData.data.filter(
          (n: NotificationResponseDto) => !n.isRead,
        ).length;

        setStoredCount(unreadCount);
        queryClient.setQueryData([NOTIFICATIONS_COUNT_KEY], unreadCount);
      }
    },
    onSettled: () => {
      // Invalidate queries to ensure data is fresh
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_COUNT_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_LIST_KEY] });
    },
  });
};

/**
 * Hook for deleting a notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationsService.deleteNotification(notificationId),
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_LIST_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_COUNT_KEY] });

      // Get previous data
      const previousData = queryClient.getQueryData([
        NOTIFICATIONS_LIST_KEY,
      ]) as NotificationListResponseDto | undefined;

      if (previousData) {
        // Find if the deleted notification was unread
        const deletedNotification = previousData.data.find(
          (n: NotificationResponseDto) => n.id === notificationId,
        );

        // If it was unread, decrement the count
        if (deletedNotification && !deletedNotification.isRead) {
          const previousCount = queryClient.getQueryData([
            NOTIFICATIONS_COUNT_KEY,
          ]) as number;
          const newCount = Math.max(0, previousCount - 1);
          setStoredCount(newCount);
          queryClient.setQueryData([NOTIFICATIONS_COUNT_KEY], newCount);
        }

        // Optimistically update list
        const updatedData = {
          ...previousData,
          data: previousData.data.filter(
            (n: NotificationResponseDto) => n.id !== notificationId,
          ),
        };

        queryClient.setQueryData([NOTIFICATIONS_LIST_KEY], updatedData);
      }

      return { previousData };
    },
    onError: (error, variables, context) => {
      // Restore previous data
      if (context?.previousData) {
        queryClient.setQueryData(
          [NOTIFICATIONS_LIST_KEY],
          context.previousData,
        );

        // Recalculate unread count
        const unreadCount = context.previousData.data.filter(
          (n: NotificationResponseDto) => !n.isRead,
        ).length;

        setStoredCount(unreadCount);
        queryClient.setQueryData([NOTIFICATIONS_COUNT_KEY], unreadCount);
      }
    },
    onSettled: () => {
      // Invalidate queries to ensure data is fresh
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_COUNT_KEY] });
    },
  });
};

/**
 * Hook for fetching notification settings
 */
export const useNotificationSettings = () => {
  return useQuery({
    queryKey: [NOTIFICATION_SETTINGS_KEY],
    queryFn: () => notificationsService.getNotificationSettings(),
  });
};

/**
 * Hook for updating notification settings
 */
export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      settings: Parameters<
        typeof notificationsService.updateNotificationSettings
      >[0],
    ) => notificationsService.updateNotificationSettings(settings),
    onSuccess: (data) => {
      // Update the settings in the cache
      queryClient.setQueryData([NOTIFICATION_SETTINGS_KEY], data);
    },
  });
};

export const useNotifications = () => {
  const count = useNotificationCount();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();

  return {
    count,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
};

export default useNotifications;
